/**
 * The soft-tissue cards — ADR-038 J2, the Body tab's "Soft tissue" section:
 * one card per region (left / right breast, belly, left / right glute), the
 * tail-spring card's sibling and in its idiom.
 *
 *  - OFFERED (the detector measured a mass, strong or weak): "Add bounce
 *    leaf" (optionally over a zone a person called soft tissue). A `none`
 *    reading is not offered — a zone can still be picked.
 *  - GROWN (or adopted from the file): on/off; the LIVE knobs (stiffness,
 *    drag, gravity) that the solver plays now; the RECORDED knobs (bounce,
 *    sway, max effect) that ride the file for the layered drive, marked so in
 *    FORM (a dim italic label); "weights" (the leaf's heat in the weight
 *    view), "remove leaf".
 *
 * READINGS ARE SAID, NOT PRINTED (XD 2026-09-29 §3B, Allen: "i tend to lean
 * status"): the leaf, the kernel's measurement, the provenance and the
 * colliders reach the status line through `say` on hover, focus and after an
 * act. The panel keeps only state and decisions.
 *
 * A pure view over `deps`: it renders what it is handed and reports input.
 * Depends on: panel-registry (types), the soft-tissue spring view type.
 * Depended on by: main.
 */
import type { PanelMount } from './panel-registry.js';
import { describeButton, withTip } from './tooltip.js';
import type { SoftTissueSpringView } from '../jiggle/soft-tissue-spring.js';
import {
  kindOfRegion,
  softTissuePreset,
  SOFT_TISSUE_DEFAULTS,
  SOFT_TISSUE_PRESETS,
  type SoftTissueControls,
  type SoftTissuePreset,
  type SoftTissueRegionId,
  type SoftTissueTier,
} from '@harryhausen/kernel';

/**
 * THE NOVICE CARD (Allen, 2026-09-27: "1 ton of information in those controls —
 * needs an XD love solution… combine left and right breasts… simplified
 * shorthand for bouncy, perky, etc."): one card per MASS — Chest (both
 * breasts), Belly, Glutes (both) — with Add, on/off, four measured presets and
 * one Amount (and Jiggle, where the mass has surface modes — Allen,
 * 2026-09-29); settle, remove and every per-side knob sit behind "Fine-tune",
 * the expert's second visit, which Creator hides outright (body-panel.css).
 */
const GROUPS: readonly { readonly id: string; readonly label: string; readonly regions: readonly SoftTissueRegionId[] }[] = [
  { id: 'chest', label: 'Chest', regions: ['LeftBreast', 'RightBreast'] },
  { id: 'belly', label: 'Belly', regions: ['Belly'] },
  { id: 'glutes', label: 'Glutes', regions: ['LeftGlute', 'RightGlute'] },
];
/** The masses a person names (one card each) — the Body header counts these, not the sides. */
export const SOFT_TISSUE_MASSES = GROUPS;
const PRESET_LABEL: Readonly<Record<SoftTissuePreset, { label: string; tip: string }>> = {
  firm: { label: 'Firm', tip: 'quick and tight — about 5–6 bounces a second, settles in two' },
  natural: { label: 'Natural', tip: 'the default — about 4 a second, rings three times' },
  bouncy: { label: 'Bouncy', tip: 'slower and longer — about 3 a second, rings four times' },
  heavy: { label: 'Heavy', tip: 'slow, big and a little saggy while it swings' },
};
const near = (a: number, b: number): boolean => Math.abs(a - b) < 1e-6;

export interface SoftTissueCardView {
  readonly region: SoftTissueRegionId;
  /** `offered` / `quiet` (measured, not offered) / `grown` / `file` / `unmeasured` / `native` (the body wears its own bones). */
  readonly state: 'offered' | 'quiet' | 'grown' | 'file' | 'unmeasured' | 'native';
  readonly tier: SoftTissueTier | null;
  readonly reading: string;
  /** Zones a person called soft tissue, for "grow over this zone". */
  readonly zones: readonly { readonly id: string; readonly name: string }[];
  /** The zone the leaf was declared on, when it was. */
  readonly zoneId: string | null;
  readonly spring: SoftTissueSpringView | null;
  /** The leaf's rig bone, for the weight view. */
  readonly leafBone: string | null;
}

export interface SoftTissuePanelView {
  readonly cards: readonly SoftTissueCardView[];
  // The standing `note` above the cards ("Bounce is offered where the body carries a mass…") is
  // DELETED (XD 2026-09-29 §3B): it was help, not state; its words ride the Add bounce tip.
}

/** A small word beside a title — the status-pill form (status-mark.ts), for a state a decision needs. */
function pill(word: string, state: 'fine' | 'check'): HTMLElement {
  const el = document.createElement('span');
  el.className = 'status-pill';
  el.dataset['state'] = state;
  el.textContent = word;
  return el;
}

export interface SoftTissuePanelDeps {
  /**
   * ONE RECEIPT (XD note 2026-09-27 §3.1, PR 4): the status mark for a card's subject, read from the
   * same Body progress the section headers draw — so a card and its header can never disagree.
   * null = the object is not counted (absent).
   */
  readonly mark?: (subject: string) => HTMLElement | null;

  readonly view: () => SoftTissuePanelView;
  readonly declare: (region: SoftTissueRegionId, zoneId: string | null) => void;
  readonly withdraw: (region: SoftTissueRegionId) => void;
  readonly setOn: (region: SoftTissueRegionId, on: boolean) => void;
  readonly setControls: (region: SoftTissueRegionId, controls: Partial<SoftTissueControls>) => void;
  readonly reset: () => void;
  readonly showWeights: (bone: string) => void;
  readonly subjectChanged?: (listener: () => void) => () => void;
  /** ADR-038 §14 J4: does this mass have surface modes? Absent or false = no Jiggle slider (no disabled promises). */
  readonly surfaceJiggle?: (region: SoftTissueRegionId) => boolean;
  /**
   * THE BODY VOICE (XD 2026-09-29 §3B; Allen: "i tend to lean status"): every reading — the leaf,
   * the kernel's measurement, the provenance, the colliders — goes to the status line on hover and
   * focus, and after an act. `reveal` for a refusal only (an unmeasured side's why).
   */
  readonly say?: (line: string | null, reveal?: boolean) => void;
}

const LABEL: Readonly<Record<SoftTissueRegionId, string>> = {
  LeftBreast: 'Left breast',
  RightBreast: 'Right breast',
  Belly: 'Belly',
  LeftGlute: 'Left glute',
  RightGlute: 'Right glute',
};

type Knob = { key: keyof SoftTissueControls; label: string; min: number; max: number; step: number; tip: string };
const KNOBS: readonly Knob[] = [
  { key: 'bounce', label: 'Bounce', min: 0, max: 3, step: 0.05, tip: 'up/down swing — 1 is the physics as solved, 3 triples it' },
  { key: 'sway', label: 'Sway', min: 0, max: 3, step: 0.05, tip: 'side-to-side swing — 1 is the physics as solved, 3 triples it' },
  { key: 'jiggle', label: 'Jiggle', min: 0, max: 3, step: 0.05, tip: 'the surface ripple as the bounce dies — 0 is off, 2 the default' },
  { key: 'stiffness', label: 'Firmness', min: 0, max: 4, step: 0.05, tip: 'how hard the mass springs back — LOWER is looser and bouncier' },
  { key: 'drag', label: 'Drag', min: 0, max: 1, step: 0.01, tip: 'how much of its swing the mass loses each step — live' },
  { key: 'gravity', label: 'Gravity', min: 0, max: 0.2, step: 0.005, tip: 'how hard a swinging mass is pulled down (none at rest)' },
  { key: 'maxEffectDeg', label: 'Max effect', min: 0, max: 90, step: 1, tip: 'the largest swing, degrees — raise it when a big move hits a wall' },
];

export function createSoftTissuePanel(deps: SoftTissuePanelDeps): PanelMount {
  return (container, store) => {
    const root = document.createElement('div');
    root.className = 'soft-tissue-panel';
    root.dataset['testid'] = 'soft-tissue';
    container.append(root);
    /** The region an act (Add, remove) just touched: its mass says its new line on the next render. */
    let actedOn: SoftTissueRegionId | null = null;
    /** Say a card's line on hover and focus (the DCC status-bar idiom, XD 2026-09-29 §6 Q3). */
    const speaks = (el: HTMLElement, line: () => string, reveal = false): void => {
      if (deps.say === undefined) return;
      const say = deps.say;
      // The INNERMOST card speaks — a side inside Fine-tune is not talked over by its mass — and
      // pointerover (it bubbles; pointerenter does not) re-says the mass when the pointer leaves a
      // side back onto it. Only a crossing INTO this card speaks, never a move within it.
      const own = (t: EventTarget | null): boolean => t instanceof Element && t.closest('.hand-card') === el;
      el.addEventListener('pointerover', (event) => {
        if (own(event.target) && !own(event.relatedTarget)) say(line(), reveal);
      });
      el.addEventListener('focusin', (event) => {
        if (own(event.target)) say(line(), reveal);
      });
    };

    /** A side's reading, said (never printed): what it is, where its spring came from, what it meets. */
    const sideLine = (c: SoftTissueCardView): string => {
      const parts = [`${LABEL[c.region]}: ${c.reading}`];
      const s = c.spring;
      if ((c.state === 'grown' || c.state === 'file') && s !== null) {
        parts.push(`spring ${s.on ? 'on' : 'off'} — ${c.state === 'file' ? 'as the file declares' : 'from the region prior'}`);
        parts.push(s.colliders.length === 0 ? 'No colliders.' : `Collides with ${s.colliders.map((x) => `${x.note} (${x.kind} ${x.radiusCm} cm)`).join('; ')}.`);
      }
      return parts.join(' · ');
    };

    const card = (c: SoftTissueCardView): HTMLElement => {
      const el = document.createElement('section');
      el.className = 'hand-card soft-tissue-card';
      el.dataset['region'] = c.region;
      el.dataset['state'] = c.state;
      const title = document.createElement('h3');
      title.textContent = LABEL[c.region];
      el.append(title);
      // THE READING IS SAID, NOT PRINTED (XD 2026-09-29 §3B): "Leaf on X, 8.3 cm root to apex…",
      // the kernel's "offered (weak) — apex 3.5 cm proud…", the provenance and the colliders all go
      // to the status line when the side is hovered or focused. What stays is FORM: the one pill a
      // decision needs now — `not measured`, whose why is revealed (a refusal must reach the eye).
      speaks(el, () => sideLine(c), c.state === 'unmeasured');
      if (c.state === 'unmeasured') title.append(' ', pill('not measured', 'check'));
      if (c.state === 'native') title.append(' ', pill('own bones', 'fine'));

      if (c.state === 'offered' || c.state === 'quiet') {
        let zone: string | null = null;
        if (c.zones.length > 0) {
          const pick = document.createElement('select');
          pick.setAttribute('aria-label', `${LABEL[c.region]} zone`);
          pick.append(new Option('as measured', ''), ...c.zones.map((z) => new Option(`over zone "${z.name}"`, z.id)));
          pick.addEventListener('change', () => {
            zone = pick.value === '' ? null : pick.value;
          });
          el.append(pick);
        }
        if (c.state === 'offered' || c.zones.length > 0) {
          const add = document.createElement('button');
          add.type = 'button';
          add.className = 'hand-apply';
          add.textContent = 'Add bounce leaf';
          add.addEventListener('click', () => {
            actedOn = c.region;
            deps.declare(c.region, zone);
          });
          el.append(...withTip(add, 'graft one leaf bone at this mass’s root, weighted to it with a feathered falloff, sprung'));
        }
        return el;
      }
      const s = c.spring;
      if ((c.state === 'grown' || c.state === 'file') && s !== null) {
        const onRow = document.createElement('label');
        onRow.className = 'tail-spring-on';
        const on = document.createElement('input');
        on.type = 'checkbox';
        on.checked = s.on;
        on.setAttribute('aria-label', `${c.region} spring on`);
        on.addEventListener('change', () => {
          deps.setOn(c.region, on.checked);
          render();
        });
        // " on" alone: the provenance is said (sideLine). The export line is HELP — the on-box's tip;
        // its "Off —" twin is DELETED (the empty box says the export declares nothing).
        onRow.append(on, document.createTextNode(' on'));
        el.append(...withTip(onRow, 'Exports as VRMC_springBone 1.0 (firmness, drag, gravity, mass); bounce, sway and max effect ride the file for World’s layered drive.'));
        for (const k of KNOBS) {
          // Jiggle lives on the MASS card now (one control per concept — see groupCard).
          if (k.key === 'jiggle') continue;
          const live = s.live.includes(k.key);
          const row = document.createElement('div');
          row.className = 'value-row tail-spring-knob';
          const label = document.createElement('span');
          // RECORDED, NOT PREVIEWED — said in FORM, not in a "·rec" suffix: the dim italic label
          // (the .pin-proposed idiom: never let a thing look like more than it is), and a tip.
          label.className = live ? 'vr-label' : 'vr-label vr-label-rec';
          label.textContent = k.label;
          const slider = document.createElement('input');
          slider.type = 'range';
          slider.min = String(k.min);
          slider.max = String(k.max);
          slider.step = String(k.step);
          slider.value = String(s.controls[k.key]);
          slider.disabled = !s.on;
          slider.setAttribute('aria-label', `${c.region} ${k.key}`);
          const value = document.createElement('span');
          value.className = 'vr-field';
          const fmt = (x: number): string => x.toFixed(k.step < 0.01 ? 3 : k.step >= 1 ? 0 : 2);
          value.textContent = fmt(Number(s.controls[k.key]));
          slider.addEventListener('input', () => {
            value.textContent = fmt(Number(slider.value));
            deps.setControls(c.region, { [k.key]: Number(slider.value) } as Partial<SoftTissueControls>);
          });
          row.append(...(live ? [label] : withTip(label, 'recorded for World — no preview here')), ...withTip(slider, k.tip), value);
          el.append(row);
        }
        const actions = document.createElement('div');
        actions.className = 'soft-tissue-actions';
        const weights = document.createElement('button');
        weights.type = 'button';
        weights.className = 'hand-revert';
        weights.textContent = 'weights';
        weights.disabled = c.leafBone === null;
        weights.addEventListener('click', () => {
          if (c.leafBone !== null) deps.showWeights(c.leafBone);
        });
        actions.append(...withTip(weights, 'show this leaf’s weights in the weight view (W hides it)'));
        // settle is ONE concept, once per Fine-tune (the group's, at the top) — not per side (§3C).
        if (c.state === 'grown') {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'hand-revert';
          remove.textContent = 'remove leaf';
          remove.addEventListener('click', () => {
            actedOn = c.region;
            deps.withdraw(c.region);
          });
          actions.append(...withTip(remove, 'withdraw this leaf — the flesh goes back to the body’s own weights (undoable)'));
        }
        el.append(actions);
      }
      return el;
    };

    /** Which preset the controls wear (their rate, ring and weight), or null (fine-tuned). */
    const presetOf = (region: SoftTissueRegionId, c: SoftTissueControls): SoftTissuePreset | null =>
      SOFT_TISSUE_PRESETS.find((p) => {
        const q = softTissuePreset(kindOfRegion(region), p);
        return near(q.stiffness, c.stiffness) && near(q.drag, c.drag) && near(q.mass, c.mass);
      }) ?? null;

    /**
     * The mass's line for the status bar: the partial / offered / own-bones sentence that used to
     * sit on the tile (§3B), then each side's reading — so hovering a grown Chest and pressing `
     * shows the leaf reading (the PR 2 field gate).
     */
    const groupLine = (g: (typeof GROUPS)[number], cards: readonly SoftTissueCardView[]): string => {
      const live = cards.filter((c) => (c.state === 'grown' || c.state === 'file') && c.spring !== null);
      const offered = cards.filter((c) => c.state === 'offered');
      let head = '';
      if (live.length > 0) {
        const left = cards.filter((c) => !live.includes(c));
        const why = left.map((c) =>
          c.state === 'offered'
            ? `the ${LABEL[c.region].toLowerCase()} can be added in Fine-tune`
            : c.state === 'quiet'
              ? `the ${LABEL[c.region].toLowerCase()} measured too small to offer`
              : `the ${LABEL[c.region].toLowerCase()} was not measured`,
        );
        // "Bouncing." alone is DELETED (the mark says it); only the partial case is worth a line.
        if (left.length > 0) head = `Bouncing on the ${live.map((c) => LABEL[c.region].toLowerCase()).join(' and ')}; ${why.join(', ')}.`;
      } else if (offered.length > 0) {
        head = `A mass was measured here${offered.some((c) => c.tier === 'strong') ? '' : ' (a small one)'}.`;
      } else if (cards.some((c) => c.state === 'native')) {
        head = 'This body carries its own bones for this — they are its spring.';
      }
      // Each side's reading only — its provenance and colliders are the SIDE's hover (sideLine), or a
      // hovered Chest wraps the status line three deep (measured on Rita, 2026-09-29).
      return [`${g.label}${head === '' ? '' : `: ${head}`}`, ...cards.map((c) => `${LABEL[c.region]}: ${c.reading}`)].join(' · ');
    };

    const groupCard = (g: (typeof GROUPS)[number], cards: readonly SoftTissueCardView[]): HTMLElement => {
      const el = document.createElement('section');
      el.className = 'hand-card soft-tissue-group';
      el.dataset['group'] = g.id;
      const title = document.createElement('h3');
      title.textContent = g.label;
      const massMark = deps.mark?.(g.label) ?? null;
      if (massMark !== null) title.append(massMark);
      el.append(title);
      const live = cards.filter((c) => (c.state === 'grown' || c.state === 'file') && c.spring !== null);
      const offered = cards.filter((c) => c.state === 'offered');
      // Offered, not yet added: the card wears the dashed border (the open-offer form).
      if (live.length === 0 && offered.length > 0) el.classList.add('soft-tissue-offer');
      // NO SUMMARY PARAGRAPH (XD 2026-09-29 §3B): the dashed tile + the mark is the state; every
      // sentence that used to sit here is said on hover (groupLine). What stays is one pill where a
      // decision hangs on it — `own bones` (nothing to add: the body's own bones are its spring).
      speaks(el, () => groupLine(g, cards));
      // The result of an act (Add, remove) is said once the rebuilt card is on screen: the new
      // reading arrives with the re-render, not with the click.
      if (actedOn !== null && g.regions.includes(actedOn)) {
        actedOn = null;
        deps.say?.(groupLine(g, cards));
      }
      if (live.length === 0 && offered.length === 0 && cards.some((c) => c.state === 'native')) {
        title.append(' ', pill('own bones', 'fine'));
      } else if (live.length === 0 && offered.length === 0) {
        // An expert's path only (a zone marked soft tissue): Creator hides this quiet card. Its how-to
        // is HELP — the `?` beside the title — not a line on the tile.
        el.classList.add('soft-tissue-quiet');
        title.append(' ', describeButton(`${g.label} soft tissue`, () => 'Nothing measured to bounce here — Fine-tune can grow it over a zone you marked.'));
      }

      if (live.length === 0 && offered.length > 0) {
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'hand-apply';
        add.textContent = 'Add bounce';
        add.addEventListener('click', () => {
          actedOn = offered[0]!.region;
          offered.forEach((c) => deps.declare(c.region, null));
        });
        // The panel's old standing note ("Bounce is offered where…") is HELP; its home is here,
        // on the one control an offered mass shows (§3B: deleted from the panel in both registers).
        el.append(
          ...withTip(
            add,
            `grow a bounce leaf in the ${offered.map((c) => LABEL[c.region].toLowerCase()).join(' and ')} (undoable). Bounce is offered where the body carries a mass; nothing moves until you add it.`,
          ),
        );
      }
      if (live.length > 0) {
        const allOn = live.every((c) => c.spring!.on);
        const onRow = document.createElement('label');
        onRow.className = 'tail-spring-on';
        const on = document.createElement('input');
        on.type = 'checkbox';
        on.checked = allOn;
        on.setAttribute('aria-label', `${g.label} bounce on`);
        on.addEventListener('change', () => {
          live.forEach((c) => deps.setOn(c.region, on.checked));
          render();
        });
        onRow.append(on, document.createTextNode(' on'));
        el.append(onRow);

        const chips = document.createElement('div');
        chips.className = 'soft-tissue-presets';
        const current = presetOf(live[0]!.region, live[0]!.spring!.controls);
        for (const p of SOFT_TISSUE_PRESETS) {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = p === current ? 'hand-apply' : 'hand-revert';
          chip.textContent = PRESET_LABEL[p].label;
          chip.disabled = !allOn;
          chip.setAttribute('aria-pressed', String(p === current));
          chip.addEventListener('click', () => {
            live.forEach((c) => deps.setControls(c.region, { ...softTissuePreset(kindOfRegion(c.region), p), on: c.spring!.on }));
            render();
          });
          chips.append(...withTip(chip, PRESET_LABEL[p].tip));
        }
        el.append(chips);

        // AMOUNT: bounce, with sway riding at the region's own ratio.
        el.append(
          massSlider(g, live, 'Amount', 'amount', live[0]!.spring!.controls.bounce, 'how far the mass swings — 1 is the physics as solved, 3 triples it', allOn, (c, a) => {
            const d = SOFT_TISSUE_DEFAULTS[kindOfRegion(c.region)];
            return { bounce: a, sway: Math.min(3, (a * d.sway) / d.bounce) };
          }),
        );
        // JIGGLE rides up onto the mass (Allen, 2026-09-29): Fine-tune is hidden in Creator, and the
        // J4 surface ripple is a thing a Creator reaches for — so it sits beside Amount, ONE control
        // per concept (the per-side copy is gone), applied to every side like Amount, and only where
        // the mass HAS surface modes (no disabled promises).
        const rippling = live.filter((c) => deps.surfaceJiggle?.(c.region) === true);
        if (rippling.length > 0) {
          const jiggle = KNOBS.find((k) => k.key === 'jiggle')!;
          el.append(massSlider(g, live, jiggle.label, 'jiggle', rippling[0]!.spring!.controls.jiggle, jiggle.tip, allOn, (_c, a) => ({ jiggle: a })));
        }
      }

      const more = document.createElement('details');
      more.className = 'soft-tissue-finetune';
      const sum = document.createElement('summary');
      sum.textContent = 'Fine-tune';
      more.append(sum);
      if (live.length > 0) {
        // settle ONCE per Fine-tune, and remove behind it (§3C): the Creator's reversal is the
        // on-box and undo, and off exports no spring — so the first view stays honest without them.
        const actions = document.createElement('div');
        actions.className = 'soft-tissue-actions';
        const settle = document.createElement('button');
        settle.type = 'button';
        settle.className = 'hand-revert';
        settle.textContent = 'settle';
        settle.addEventListener('click', () => deps.reset());
        actions.append(...withTip(settle, 'put every leaf back at rest and let it go again'));
        const grown = live.filter((c) => c.state === 'grown');
        if (grown.length > 0) {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'hand-revert';
          remove.textContent = 'remove';
          remove.addEventListener('click', () => {
            actedOn = grown[0]!.region;
            grown.forEach((c) => deps.withdraw(c.region));
          });
          actions.append(...withTip(remove, 'withdraw the bounce here — the flesh goes back to the body’s own weights (undoable)'));
        }
        more.append(actions);
      }
      for (const c of cards) more.append(card(c));
      el.append(more);
      return el;
    };

    /** One mass-level slider (Amount, Jiggle): previews live, writes every live side of the mass. */
    const massSlider = (
      g: (typeof GROUPS)[number],
      live: readonly SoftTissueCardView[],
      labelText: string,
      aria: string,
      start: number,
      tipText: string,
      enabled: boolean,
      write: (c: SoftTissueCardView, value: number) => Partial<SoftTissueControls>,
    ): HTMLElement => {
      const row = document.createElement('div');
      row.className = 'value-row tail-spring-knob';
      const label = document.createElement('span');
      label.className = 'vr-label';
      label.textContent = labelText;
      const slider = document.createElement('input');
      slider.type = 'range';
      slider.min = '0';
      slider.max = '3';
      slider.step = '0.05';
      slider.value = String(start);
      slider.disabled = !enabled;
      slider.setAttribute('aria-label', `${g.label} ${aria}`);
      const value = document.createElement('span');
      value.className = 'vr-field';
      value.textContent = Number(slider.value).toFixed(2);
      slider.addEventListener('input', () => {
        const a = Number(slider.value);
        value.textContent = a.toFixed(2);
        live.forEach((c) => deps.setControls(c.region, write(c, a)));
      });
      row.append(label, ...withTip(slider, tipText), value);
      return row;
    };

    const render = (): void => {
      const v = deps.view();
      // Keep an open Fine-tune disclosure open across a re-render.
      const open = new Set(
        [...root.querySelectorAll<HTMLElement>('.soft-tissue-group')]
          .filter((el) => el.querySelector('details')?.open === true)
          .map((el) => el.dataset['group']),
      );
      root.replaceChildren();
      for (const g of GROUPS) {
        const cards = g.regions.map((r) => v.cards.find((c) => c.region === r)).filter((c): c is SoftTissueCardView => c !== undefined);
        if (cards.length === 0) continue;
        const el = groupCard(g, cards);
        if (open.has(g.id)) el.querySelector('details')!.open = true;
        root.append(el);
      }
    };
    const unsubscribe = store.subscribe(render);
    const unsubscribeSubject = deps.subjectChanged?.(render) ?? (() => undefined);
    render();
    return {
      dispose() {
        unsubscribe();
        unsubscribeSubject();
        root.remove();
      },
    };
  };
}
